import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { ShieldCheck, User as UserIcon, Menu, X } from "lucide-react";
import NotificationBell from "@/components/NotificationBell";

const NAV_LINKS = [
  { to: "/", label: "Plateforme", testid: "nav-home" },
  { to: "/pour-qui", label: "Pour qui ?", testid: "nav-forwho" },
  { to: "/artisan", label: "Métiers", testid: "nav-metiers" },
  { to: "/travaux", label: "Travaux", testid: "nav-travaux" },
  { to: "/blog", label: "Blog", testid: "nav-blog" },
  { to: "/artisans", label: "Artisans", testid: "nav-artisans" },
  { to: "/estimer", label: "Estimateur", testid: "nav-estimator" },
  { to: "/pro/rejoindre", label: "Devenir artisan", testid: "nav-join-artisan" },
  { to: "/#tarifs", label: "Tarifs", testid: "nav-pricing", external: true },
];

export default function Navbar({ dark = false }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const text = dark ? "text-white" : "text-[#0A0A0B]";
  const sub = dark ? "text-zinc-400" : "text-zinc-600";
  const bg = dark ? "bg-[#0A0A0B] border-zinc-800" : "bg-white border-zinc-200";

  const closeMobile = () => setMobileOpen(false);

  return (
    <header
      data-testid="navbar"
      className={`sticky top-0 z-50 ${bg} border-b-2 ${text}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-3">
        <Link to="/" className="flex items-center gap-2 sm:gap-3 shrink-0 min-w-0" data-testid="navbar-logo" onClick={closeMobile}>
          <div className="w-8 h-8 sm:w-9 sm:h-9 bg-[#FF4D00] flex items-center justify-center shrink-0">
            <ShieldCheck className="w-4 h-4 sm:w-5 sm:h-5 text-white" strokeWidth={2.5} />
          </div>
          <div className="leading-tight min-w-0">
            <div className="display text-xl sm:text-2xl tracking-tighter">BATISUR</div>
            <div className={`mono text-[9px] sm:text-[10px] uppercase ${sub} tracking-widest truncate`}>
              Paiement sécurisé · BTP
            </div>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-6 xl:gap-8 text-sm font-medium">
          {NAV_LINKS.map((l) => (
            l.external ? (
              <a key={l.to} href={l.to} className="hover:text-[#FF4D00] whitespace-nowrap" data-testid={l.testid}>{l.label}</a>
            ) : (
              <Link key={l.to} to={l.to} className="hover:text-[#FF4D00] whitespace-nowrap" data-testid={l.testid}>{l.label}</Link>
            )
          ))}
        </nav>

        {/* Right actions */}
        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          {!user && (
            <>
              <Link to="/login" data-testid="nav-login" className="hidden sm:inline-flex">
                <Button variant="ghost" className="rounded-none px-3">Connexion</Button>
              </Link>
              <Link to="/register" data-testid="nav-register" className="hidden sm:inline-flex">
                <button className="btn-batisur">S'inscrire</button>
              </Link>
            </>
          )}
          {user && (
            <>
            <NotificationBell />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-2 border-2 border-[#0A0A0B] px-2 sm:px-3 py-1.5 sm:py-2 hover:bg-[#0A0A0B] hover:text-white transition"
                  data-testid="navbar-user-menu"
                >
                  <UserIcon className="w-4 h-4" />
                  <span className="text-sm font-medium max-w-[100px] truncate hidden sm:inline">{user.name}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-none w-56">
                <DropdownMenuLabel className="mono text-[10px] uppercase tracking-wider text-zinc-500">
                  {user.role}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => nav("/dashboard")} data-testid="menu-dashboard">
                  Mon tableau de bord
                </DropdownMenuItem>
                {(user.role === "demandeur" || user.role === "artisan") && (
                  <DropdownMenuItem onClick={() => nav("/invitations")} data-testid="menu-invitations">
                    Inviter une partie
                  </DropdownMenuItem>
                )}
                {user.role === "artisan" && (
                  <DropdownMenuItem onClick={() => nav("/pro/premium")} data-testid="menu-premium">
                    Pack Premium ★
                  </DropdownMenuItem>
                )}
                {user.role === "admin" && (
                  <DropdownMenuItem onClick={() => nav("/admin")} data-testid="menu-admin">
                    Espace admin
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={async () => { await logout(); nav("/"); }} data-testid="nav-logout">
                  Déconnexion
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            </>
          )}

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileOpen((o) => !o)}
            className="lg:hidden border-2 border-[#0A0A0B] p-2 hover:bg-[#0A0A0B] hover:text-white transition"
            aria-label="Menu"
            data-testid="nav-mobile-toggle"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu drawer */}
      {mobileOpen && (
        <div className={`lg:hidden ${bg} border-t-2 border-[#0A0A0B]`} data-testid="nav-mobile-drawer">
          <nav className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-col gap-1">
            {NAV_LINKS.map((l) => (
              l.external ? (
                <a
                  key={l.to}
                  href={l.to}
                  className="py-3 px-3 border-b border-zinc-200 text-sm font-medium hover:bg-zinc-50 hover:text-[#FF4D00] mono uppercase tracking-widest"
                  onClick={closeMobile}
                  data-testid={`m-${l.testid}`}
                >
                  {l.label}
                </a>
              ) : (
                <Link
                  key={l.to}
                  to={l.to}
                  className="py-3 px-3 border-b border-zinc-200 text-sm font-medium hover:bg-zinc-50 hover:text-[#FF4D00] mono uppercase tracking-widest"
                  onClick={closeMobile}
                  data-testid={`m-${l.testid}`}
                >
                  {l.label}
                </Link>
              )
            ))}
            {!user && (
              <div className="mt-4 flex flex-col gap-2 sm:hidden">
                <Link to="/login" onClick={closeMobile} className="text-center border-2 border-[#0A0A0B] py-3 mono text-[11px] uppercase tracking-widest" data-testid="m-nav-login">
                  Connexion
                </Link>
                <Link to="/register" onClick={closeMobile} className="btn-batisur text-center" data-testid="m-nav-register">
                  S'inscrire gratuitement
                </Link>
              </div>
            )}
            <button
              onClick={() => {
                closeMobile();
                localStorage.removeItem("batisur_pwa_install_dismissed");
                window.dispatchEvent(new Event("batisur:show-pwa"));
              }}
              className="mt-4 flex items-center justify-center gap-2 border-2 border-[#FF4D00] text-[#FF4D00] py-3 mono text-[11px] uppercase tracking-widest hover:bg-[#FF4D00] hover:text-white"
              data-testid="m-nav-install-app"
            >
              <Menu className="w-4 h-4" /> Installer l'app Batisur
            </button>
          </nav>
        </div>
      )}
    </header>
  );
}
